import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  InputNumber,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";
import PropTypes from "prop-types";

import { DownloadOutlined, MoreOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import ManagePlanModal from "../../Components/ManagePlanModal";
import ManageTagUntagPartnerModal from "../../Components/ManageTagUntagPartnerModal";
import RefreshEarningsButton from "../../Components/RefreshEarningsButton/RefreshEarningsButton";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import UpdateGstDetailsModal from "../../Components/UpdateGstDetailsModal";
import { useAppTheme } from "../../context/ThemeProvider";
import useDebounce from "../../hooks/useDebounce";
import {
  AUTOPAY_STATUS_MAP,
  DATE_TIME_FORMAT,
  DEFAULT_DATES_RANGE_OPTIONS,
  DOMAIN_STATUS_MAP,
  PAYMENT_PLAN_COLORS,
  USER_TIER_TITLE,
} from "../../Utils/constants";
import { showAppError } from "../../Utils/error";
import { numFormatter } from "../../Utils/formatter";
import debounce from "../../Utils/utilityFunctions";
import {
  cancelTierSubscription,
  getWhitelabelCreatorsData,
  suspendPlatform,
  updateWLExpiry,
  upgradeTier,
} from "../api";
import CreatorDetailExpand from "./CreatorDetailExpand";

const { Option } = Select;

const FreedomCreators = ({ sensitiveAllowedActions }) => {
  const { COLORS } = useAppTheme();

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [earningGt, setEarningGt] = useState(null);
  const [earningLt, setEarningLt] = useState(null);
  const [mangoCountGt, setMangoCountGt] = useState(null);
  const [mangoCountLt, setMangoCountLt] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tierUpgradeModalVisible, setTierUpgradeModalVisible] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectPaymentPlan, setSelectPaymentPlan] = useState(null);
  const [filterTier, setFilterTier] = useState(null);
  const [filterDomainStatus, setFilterDomainStatus] = useState(null);
  const [totalDomainActivated, setTotalDomainActivated] = useState(0);
  const [totalSuspended, setTotalSuspended] = useState(0);
  const [tierExpiryStartDate, setTierExpiryStartDate] = useState(null);
  const [tierExpiryEndDate, setTierExpiryEndDate] = useState(null);
  const [suspendPlatformModalVisible, setSuspendPlatformModalVisible] =
    useState(false);
  const [tierExpiryStatus, setTierExpiryStatus] = useState(null);
  const [tierExpiryUpdateModalVisible, setTierExpiryUpdateModalVisible] =
    useState(false);
  const [selectedTierExpiryDate, setSelectedTierExpiryDate] = useState(null);
  const [platformSuspensionStatus, setPlatformSuspensionStatus] =
    useState(null);
  const [tierAutopayStatus, setTierAutopayStatus] = useState(null);
  const [cancelSubscriptionModalVisible, setCancelSubscriptionModalVisible] =
    useState(false);
  const [performSuspendPlatform, setPerformSuspendPlatform] = useState(false);
  const [managePlanModalVisible, setManagePlanModalVisible] = useState(false);
  const [partnerTagUntagModalVisible, setPartnerTagUntagModalVisible] =
    useState(false);
  const [gstDetailsModalVisible, setGstDetailsModalVisible] = useState(false);

  const [onetimePer, setOnetimePer] = useState(null);
  const [recurringPer, setRecurringPer] = useState(null);
  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });
  const [tableKey, setTableKey] = useState(Date.now());
  const controllerRef = useRef(null);

  const autopayStatuses = [
    "pending",
    "halted",
    "cancelled",
    "paused",
    "expired",
    "completed",
    "active",
    "authenticated",
  ];

  const sensitiveAllowedFunctionSet = React.useMemo(
    () => new Set(sensitiveAllowedActions),
    [sensitiveAllowedActions]
  );

  const creatorsTable = () => {
    const columns = [
      {
        title: "Creator",
        dataIndex: "name",
        key: "name",
        render: (_, record) => {
          return (
            <TableUserInfoItem
              name={record?.name}
              profilePic={record?.profilePicUrl}
              hideContact
            />
          );
        },
      },
      {
        title: "Host",
        dataIndex: "host",
        key: "host",
        render: (text, record) => (
          <>
            <Typography.Link href={`https://${text}`}>{text}</Typography.Link>
            <Typography>
              <strong style={{ marginRight: 8 }}>Domain status:</strong>
              <Tag color={DOMAIN_STATUS_MAP[record?.domainStatus]?.color}>
                {DOMAIN_STATUS_MAP[record?.domainStatus]?.text}
              </Tag>
            </Typography>
            <Typography>
              <strong style={{ marginRight: 8 }}>
                Domain status updated on:
              </strong>
              <span>
                {record?.domainStatusUpdatedAt
                  ? moment(record?.domainStatusUpdatedAt).format(
                      DATE_TIME_FORMAT
                    )
                  : "-"}
              </span>
            </Typography>
          </>
        ),
      },
      {
        title: "User Tier",
        dataIndex: "userTier",
        key: "userTier",
        render: (userTier) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {USER_TIER_TITLE?.[userTier] || "-"}
          </span>
        ),
      },
      {
        title: "Payment Plan",
        dataIndex: "paymentPlan",
        key: "paymentPlan",
        render: (paymentPlan) => {
          return (
            <Tag color={PAYMENT_PLAN_COLORS?.[paymentPlan] || "default"}>
              {paymentPlan}
            </Tag>
          );
        },
      },
      {
        title: "Mango Count",
        dataIndex: "mangoCount",
        key: "mangoCount",
        render: (mangoCount) =>
          numFormatter({ amount: mangoCount, hideCurrency: true }),
      },
      {
        title: "Total Earnings",
        dataIndex: "totalEarning",
        key: "totalEarning",
        render: (totalEarning) => numFormatter({ amount: totalEarning }),
      },
      {
        title: "Created On",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date) => (date ? moment(date).format(DATE_TIME_FORMAT) : "-"),
      },
      {
        title: "Tier Expiry Date",
        dataIndex: "accountSettingTierSubscriptionExpiry",
        key: "accountSettingTierSubscriptionExpiry",
        render: (date) => (date ? moment(date).format(DATE_TIME_FORMAT) : "-"),
      },
      {
        title: "Tier Autopay Status",
        dataIndex: "accountSettingTierRazorpaySubscriptionIsAutopayStatus",
        key: "accountSettingTierRazorpaySubscriptionIsAutopayStatus",
        render: (status) =>
          status ? (
            <Tag color={AUTOPAY_STATUS_MAP[status]?.color}>
              {AUTOPAY_STATUS_MAP[status]?.text}
            </Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "WhatsApp Autopay Status",
        dataIndex: "accountSettingWhatsappRazorpaySubscriptionIsAutopayStatus",
        key: "accountSettingWhatsappRazorpaySubscriptionIsAutopayStatus",
        render: (status) =>
          status ? (
            <Tag color={AUTOPAY_STATUS_MAP[status]?.color}>
              {AUTOPAY_STATUS_MAP[status]?.text}
            </Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "Platform Status",
        dataIndex: "platformSuspended",
        key: "platformSuspended",
        render: (status) => (
          <Tag color={status ? "error" : "success"}>
            {status ? "Suspended" : "Active"}
          </Tag>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => {
          const tier234 =
            record?.userTier === "tier2" ||
            record?.userTier === "tier3" ||
            record?.userTier === "tier4";

          const onRowActionMenuClick = ({ key, domEvent }) => {
            domEvent?.stopPropagation?.();
            setSelectedUser(record);
            if (record?.platformSuspended) {
              if (key === "activate-platform") {
                setPerformSuspendPlatform(false);
                setSuspendPlatformModalVisible(true);
              } else if (key === "update-gst") {
                setGstDetailsModalVisible(true);
              }
              return;
            }
            switch (key) {
              case "update-tier-expiry":
                setTierExpiryUpdateModalVisible(true);
                break;
              case "cancel-subscription":
                setCancelSubscriptionModalVisible(true);
                break;
              case "upgrade-tier":
                setTierUpgradeModalVisible(true);
                setOnetimePer(record?.razorpayOnetimeComission);
                setRecurringPer(record?.razorpayRecurringComission);
                setSelectedTier(record?.userTier);
                setSelectPaymentPlan(record?.paymentPlan);
                break;
              case "suspend-platform":
                setPerformSuspendPlatform(true);
                setSuspendPlatformModalVisible(true);
                break;
              case "manage-plan":
                setManagePlanModalVisible(true);
                break;
              case "partner-tag":
                setPartnerTagUntagModalVisible(true);
                break;
              case "update-gst":
                setGstDetailsModalVisible(true);
                break;
              default:
                break;
            }
          };

          const overlay = record?.platformSuspended ? (
            <Menu onClick={onRowActionMenuClick}>
              <Menu.Item key="activate-platform">Activate platform</Menu.Item>
              <Menu.Item key="update-gst">Update GST</Menu.Item>
            </Menu>
          ) : (
            <Menu onClick={onRowActionMenuClick}>
              {tier234 ? (
                <>
                  <Menu.Item key="update-tier-expiry" disabled>
                    Update tier expiry
                  </Menu.Item>
                  <Menu.Item key="cancel-subscription" disabled danger>
                    Cancel subscription
                  </Menu.Item>
                </>
              ) : null}
              <Menu.Item key="upgrade-tier" disabled>
                Upgrade tier
              </Menu.Item>
              <Menu.Item key="suspend-platform" danger>
                Suspend platform
              </Menu.Item>
              {sensitiveAllowedFunctionSet.has("managePlanButton") ? (
                <Menu.Item key="manage-plan">Manage plan</Menu.Item>
              ) : null}
              <Menu.Item key="partner-tag">Tag/Untag partner</Menu.Item>
              {sensitiveAllowedFunctionSet.has("updateGstDetails") ? (
                <Menu.Item key="update-gst">Update GST</Menu.Item>
              ) : null}
            </Menu>
          );

          return (
            <div onClick={(e) => e.stopPropagation()} role="presentation">
              <Dropdown
                overlay={overlay}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  shape="circle"
                  icon={<MoreOutlined />}
                  aria-label="Open row actions"
                  className="siteBtn circularBgHover gray"
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            </div>
          );
        },
      },
    ];

    return (
      <Table
        key={tableKey}
        columns={columns}
        dataSource={loading ? [] : users}
        pagination={{
          current: page,
          total,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        loading={loading}
        locale={{ emptyText: loading ? "Loading..." : "No users found" }}
        rowKey={(record) => record._id}
        expandable={{
          expandedRowRender: (record) => (
            <CreatorDetailExpand
              creatorId={record?._id}
              canManageSignupService={sensitiveAllowedFunctionSet.has(
                "manageSignupService"
              )}
            />
          ),
        }}
        scroll={{ x: "auto" }}
      />
    );
  };

  const handleGetWLCreators = async () => {
    setLoading(true);
    try {
      controllerRef.current = new AbortController();
      const resp = await getWhitelabelCreatorsData(
        {
          search: debouncedSearch,
          whitelabelPlanType: "freedom-plan",
          tier: filterTier,
          domainActivationStatus: filterDomainStatus,
          startDate,
          endDate,
          earningGt,
          earningLt,
          mangoCountGt,
          mangoCountLt,
          tierExpiryDateGt: tierExpiryStartDate,
          tierExpiryDateLt: tierExpiryEndDate,
          tierExpired: tierExpiryStatus,
          platformSuspensionStatus,
          tierAutopayStatus,
          page,
          limit: 10,
          csv: false,
        },
        { signal: controllerRef?.current?.signal }
      );

      if (resp.code === 0) {
        controllerRef.current = null;
        setUsers(resp?.result?.data);
        setTotal(resp?.result?.total);
        setTotalDomainActivated(resp?.result?.totalDomainActivated);
        setTotalSuspended(resp?.result?.totalSuspended);
        setTableKey(Date.now());
      } else {
        showAppError(resp);
      }
    } catch (err) {
      console.log(err);
      showAppError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGetWLCreatorsCSV = async () => {
    try {
      const resp = await getWhitelabelCreatorsData({
        search: debouncedSearch,
        whitelabelPlanType: "freedom-plan",
        tier: filterTier,
        domainActivationStatus: filterDomainStatus,
        startDate,
        endDate,
        earningGt,
        earningLt,
        mangoCountGt,
        mangoCountLt,
        tierExpiryDateGt: tierExpiryStartDate,
        tierExpiryDateLt: tierExpiryEndDate,
        tierExpired: tierExpiryStatus,
        platformSuspensionStatus,
        tierAutopayStatus,
        page,
        limit: 10,
        csv: true,
      });
      fileDownload(resp, `freedom-creators-${Date.now()}.csv`);
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    }
  };

  const handleTierUpgrade = async (
    userId,
    tier,
    onetime,
    recurring,
    paymentPlan,
    convenienceFee = 0
  ) => {
    const reqBody = { userId, tier, paymentPlan };
    const tier3Andtier4Threshold = 0.9;

    if (tier === "tier4") {
      // for tier 4 only
      const tmCommission = 0;
      const stripeCommission = 4.5;

      const withRazorpayOnetimeComission =
        tmCommission + convenienceFee + onetime;
      const withRazorpayRecurringComission =
        tmCommission + convenienceFee + recurring;
      const withStripeOnetimeComission =
        tmCommission + convenienceFee + stripeCommission;
      const withStripeRecurringComission =
        tmCommission + convenienceFee + stripeCommission;
      if (!onetime && !recurring) {
        message.error("Enter onetime and recurring payment gateway charges");
        return;
      }
      if (!onetime && onetime !== 0) {
        message.error("Enter onetime payment gateway charges");
        return;
      }
      if (onetime < 0 || onetime > 100) {
        message.error("Enter valid onetime payment gateway charges");
        return;
      }
      if (!recurring && recurring !== 0) {
        message.error("Enter recurring payment gateway charges");
        return;
      }
      if (recurring < 0 || recurring > 100) {
        message.error("Enter valid recurring payment gateway charges");
        return;
      }

      if (
        withRazorpayOnetimeComission < tier3Andtier4Threshold ||
        withRazorpayRecurringComission < tier3Andtier4Threshold ||
        withStripeOnetimeComission < tier3Andtier4Threshold ||
        withStripeRecurringComission < tier3Andtier4Threshold
      ) {
        message.error(
          `Onetime and recurring payment gateway charges should be greater than ${tier3Andtier4Threshold}`
        );
        return;
      }
      reqBody.razorpayOnetimeComission = Number(onetime);
      reqBody.razorpayRecurringComission = Number(recurring);
    }
    const hide = message.loading("Upgrading tier...");
    try {
      const resp = await upgradeTier(reqBody);
      if (resp.code === 0) {
        message.success("Tier upgraded successfully");
        setTierUpgradeModalVisible(false);
        setSelectedUser(null);
        setSelectedTier(null);
        setSelectPaymentPlan(null);
        setRecurringPer(null);
        setOnetimePer(null);
        handleGetWLCreators();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      hide();
    }
  };

  const handlePlatformSuspend = async (userId, suspend) => {
    const hide = message.loading(
      suspend ? "Suspending platform..." : "Un-suspending platform..."
    );
    try {
      const resp = await suspendPlatform({ userId, suspend });
      if (resp.code === 0) {
        message.success(
          suspend
            ? "Platform suspended successfully"
            : "Platform unsuspended successfully"
        );
        setSuspendPlatformModalVisible(false);
        setSelectedUser(null);
        setPerformSuspendPlatform(false);
        handleGetWLCreators();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      hide();
    }
  };

  const handleUpdateTierExpiry = async (userId, expiryDate) => {
    const hide = message.loading("Updating tier expiry...");
    try {
      const resp = await updateWLExpiry({ userId, expiryDate });
      if (resp.code === 0) {
        message.success("Tier expiry updated successfully");
        setTierExpiryUpdateModalVisible(false);
        setSelectedUser(null);
        setSelectedTierExpiryDate(null);
        handleGetWLCreators();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      hide();
    }
  };

  const handleCancelTierSubscription = async (userId) => {
    const hide = message.loading("Cancelling subscription...");
    try {
      const resp = await cancelTierSubscription({ userId });
      if (resp.code === 0) {
        message.success("Subscription cancelled successfully");
      } else {
        message.error(resp?.result || resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error(
        err?.response?.data.result || err?.message || "Something went wrong"
      );
    } finally {
      hide();
      setCancelSubscriptionModalVisible(false);
      setSelectedUser(null);
    }
  };

  useEffect(() => {
    const controller = controllerRef.current;
    if (controller?.abort) {
      controller?.abort();
    }
    handleGetWLCreators();
  }, [
    page,
    debouncedSearch,
    startDate,
    endDate,
    earningGt,
    earningLt,
    mangoCountGt,
    mangoCountLt,
    filterTier,
    filterDomainStatus,
    tierExpiryStartDate,
    tierExpiryEndDate,
    tierExpiryStatus,
    platformSuspensionStatus,
    tierAutopayStatus,
  ]);

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={8}>
          <Card>
            <Statistic title="Total Creators" value={total} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active"
              value={totalDomainActivated}
              valueStyle={{
                color: COLORS.SUCCESS500,
              }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Suspended"
              value={totalSuspended}
              valueStyle={{
                color: COLORS.ERROR500,
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col>
          <CustomSearchbar
            placeholder="Search by name, email or host"
            searchQuery={search}
            setSearchQuery={(value) => {
              setSearch(value);
              setPage(1);
            }}
            style={{ width: 500 }}
          />
        </Col>
        <Col style={{ marginLeft: "auto" }}>
          <Space>
            <RefreshEarningsButton />
            <Button
              className="siteBtn darkBtn"
              onClick={handleGetWLCreatorsCSV}
            >
              <DownloadOutlined />
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={8}>
          <Space>
            <Typography>Amount Made:</Typography>
            <InputNumber
              size="large"
              value={earningGt}
              onChange={debounce(setEarningGt)}
            />
            <Typography>To:</Typography>
            <InputNumber
              size="large"
              value={earningLt}
              onChange={debounce(setEarningLt)}
            />
          </Space>
        </Col>
        <Col span={8}>
          <Space>
            <Typography>Number of Mangoes Made:</Typography>
            <InputNumber
              size="large"
              value={mangoCountGt}
              onChange={debounce(setMangoCountGt)}
            />
            <Typography>To:</Typography>
            <InputNumber
              size="large"
              value={mangoCountLt}
              onChange={debounce(setMangoCountLt)}
            />
          </Space>
        </Col>
        <Col span={8}>
          <DatePicker.RangePicker
            size="large"
            ranges={DEFAULT_DATES_RANGE_OPTIONS}
            value={[startDate, endDate]}
            onChange={(values) => {
              setStartDate(values?.[0]);
              setEndDate(values?.[1]);
            }}
          />
        </Col>
      </Row>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={12}>
          <Space size={12} wrap>
            <Select
              size="large"
              placeholder="Select tier"
              allowClear
              value={filterTier}
              onSelect={setFilterTier}
              onClear={() => setFilterTier(null)}
            >
              <Option value="tier1">Tier 1</Option>
              <Option value="tier2">Tier 2</Option>
              <Option value="tier3">Tier 3</Option>
              <Option value="tier4">Tier 4</Option>
            </Select>
            <Select
              size="large"
              placeholder="Select domain status"
              allowClear
              value={filterDomainStatus}
              onSelect={setFilterDomainStatus}
              onClear={() => setFilterDomainStatus(null)}
            >
              <Option value="domain_verified">Domain Verified</Option>
              <Option value="initiated">Initiated</Option>
              <Option value="drafted">Drafted</Option>
            </Select>
            <DatePicker.RangePicker
              size="large"
              ranges={DEFAULT_DATES_RANGE_OPTIONS}
              placeholder={["Sub. End Start Date", "Sub. End End Date"]}
              allowClear
              value={[tierExpiryStartDate, tierExpiryEndDate]}
              onChange={(values) => {
                setTierExpiryStartDate(values?.[0]);
                setTierExpiryEndDate(values?.[1]);
              }}
              style={{ width: 350 }}
            />
            <Select
              size="large"
              placeholder="Select tier expiry status"
              allowClear
              value={tierExpiryStatus}
              onSelect={setTierExpiryStatus}
              onClear={() => setTierExpiryStatus(null)}
            >
              <Option value="expired">Expired</Option>
              <Option value="notexpired">Not Expired</Option>
            </Select>
            <Select
              size="large"
              placeholder="Select platform suspension status"
              allowClear
              value={platformSuspensionStatus}
              onSelect={setPlatformSuspensionStatus}
              onClear={() => setPlatformSuspensionStatus(null)}
            >
              <Option value="suspended">Suspended</Option>
              <Option value="notsuspended">Not Suspended</Option>
            </Select>
            <Select
              size="large"
              placeholder="Select tier autopay status"
              allowClear
              value={tierAutopayStatus}
              onSelect={setTierAutopayStatus}
              onClear={() => setTierAutopayStatus(null)}
            >
              {autopayStatuses.map((val) => (
                <Option value={val}>{val}</Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>
      {creatorsTable()}
      <Modal
        visible={tierUpgradeModalVisible}
        onCancel={() => {
          setTierUpgradeModalVisible(false);
          setSelectedUser(null);
          setSelectedTier(null);
          setRecurringPer(null);
          setOnetimePer(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Typography>
          Are you sure you want to upgrade the plan for {selectedUser?.name}?
        </Typography>
        <Space
          direction="vertical"
          style={{
            width: "100%",
          }}
        >
          <Select
            style={{
              width: "200px",
            }}
            value={selectedTier}
            onChange={(value) => setSelectedTier(value)}
          >
            <Option value="tier1">Tier 1</Option>
            <Option value="tier2">Tier 2</Option>
            <Option value="tier3">Tier 3</Option>
            <Option value="tier4">Tier 4</Option>
          </Select>
          {selectedTier === "tier4" ? (
            <>
              <Typography>TM Commission: 0%</Typography>
              <Typography>Stripe Commission: 4.5%</Typography>
              <Typography>Indian PG charges: </Typography>

              <Typography>
                {" "}
                onetime % :{" "}
                <InputNumber value={onetimePer} onChange={setOnetimePer} />
              </Typography>

              <Typography>
                {" "}
                recurring % :{" "}
                <InputNumber value={recurringPer} onChange={setRecurringPer} />
              </Typography>
            </>
          ) : null}

          <Typography>Payment Plan</Typography>
          <Select
            style={{
              width: "200px",
            }}
            value={selectPaymentPlan}
            onChange={(value) => setSelectPaymentPlan(value)}
          >
            <Option value="monthly">Monthly</Option>
            <Option value="annual">annual</Option>
          </Select>

          <Space>
            <Button
              type="primary"
              onClick={() => {
                if (selectedTier) {
                  handleTierUpgrade(
                    selectedUser?._id,
                    selectedTier,
                    onetimePer,
                    recurringPer,
                    selectPaymentPlan,
                    selectedUser?.convenienceFee
                  );
                } else {
                  message.error("Please select a plan");
                }
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                setTierUpgradeModalVisible(false);
                setSelectedUser(null);
                setOnetimePer(null);
                setRecurringPer(null);
                setSelectedTier(null);
              }}
            >
              No
            </Button>
          </Space>
        </Space>
      </Modal>
      <Modal
        visible={suspendPlatformModalVisible}
        onCancel={() => {
          setSuspendPlatformModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Typography>
          Are you sure you want to{" "}
          {performSuspendPlatform ? "suspend" : "un-suspend"} the platform for{" "}
          {selectedUser?.name}?
        </Typography>
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handlePlatformSuspend(selectedUser?._id, performSuspendPlatform);
            }}
          >
            Yes
          </Button>
          <Button
            onClick={() => {
              setTierUpgradeModalVisible(false);
              setSelectedUser(null);
            }}
          >
            No
          </Button>
        </Space>
      </Modal>
      <Modal
        visible={tierExpiryUpdateModalVisible}
        onCancel={() => {
          setTierExpiryUpdateModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Typography>
          Are you sure you want to update the tier expiry for{" "}
          {selectedUser?.name}?
        </Typography>
        <Space direction="vertical">
          <DatePicker
            value={selectedTierExpiryDate}
            onChange={(value) => setSelectedTierExpiryDate(value)}
            placeholder="Tier Expiry Date"
            style={{
              width: "200px",
            }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                handleUpdateTierExpiry(
                  selectedUser?._id,
                  selectedTierExpiryDate
                );
              }}
            >
              Save
            </Button>
            <Button
              onClick={() => {
                setTierUpgradeModalVisible(false);
                setSelectedUser(null);
                setSelectedTierExpiryDate(null);
              }}
            >
              Cancel
            </Button>
          </Space>
        </Space>
      </Modal>

      <ManagePlanModal
        visible={managePlanModalVisible}
        onClose={() => setManagePlanModalVisible(false)}
        onSave={() => {
          handleGetWLCreators();
          setManagePlanModalVisible(false);
        }}
        userId={selectedUser?._id}
        mode="normal"
      />

      <ManageTagUntagPartnerModal
        visible={partnerTagUntagModalVisible}
        onClose={() => setPartnerTagUntagModalVisible(false)}
        onSave={() => {
          setPartnerTagUntagModalVisible(false);
        }}
        userId={selectedUser?._id}
      />

      <UpdateGstDetailsModal
        visible={gstDetailsModalVisible}
        onClose={() => setGstDetailsModalVisible(false)}
        creatorId={selectedUser?._id}
        creatorName={selectedUser?.name}
        user={selectedUser}
        onSuccess={() => {
          handleGetWLCreators();
        }}
      />

      <Modal
        visible={cancelSubscriptionModalVisible}
        onCancel={() => {
          setSelectedUser(null);
          setCancelSubscriptionModalVisible(false);
        }}
        onOk={() => {
          handleCancelTierSubscription(selectedUser?._id);
        }}
        destroyOnClose
        title="Cancel Subscription?"
      >
        Are you sure you want to cancel the subscription for{" "}
        {selectedUser?.name}? THIS IS IRREVERSIBLE
      </Modal>
    </>
  );
};

FreedomCreators.propTypes = {
  sensitiveAllowedActions: PropTypes.shape([]),
};

export default FreedomCreators;
